<template>
  <div class="space-y-4">
    <div class="flex flex-col gap-4">
      <InputField name="title" :label="t('title')" required />

      <MediaUpload v-if="!initialMedia" ref="mediaUploadRef" :label="t('videoFile')" />
    </div>

    <div class="flex items-center justify-end gap-4">
      <CVButton variant="default" type="submit" :is-loading="isPending" @click="submit">
        {{ initialMedia ? t('save') : t('create') }}
      </CVButton>
      <CVButton type="button" variant="secondary" :disabled="isPending" @click="$emit('cancel')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CVButton from '@/components/buttons/CVButton.vue';
import InputField from '@/components/InputField.vue';
import MediaUpload from '@/components/media/MediaUpload.vue';
import { useCreateMedia, useUpdateMedia } from '@/queries/use-media';
import type { Media as RosMedia } from '@carvia/ros-client-types';
import type { UpdateMediaDto } from '@carvia/ros-client-types';
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

const props = defineProps<{
  initialMedia?: RosMedia;
}>();

const emit = defineEmits<{
  (e: 'saved', media: RosMedia): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();
const mediaUploadRef = ref<InstanceType<typeof MediaUpload>>();

const { mutateAsync: createMedia, isPending: isCreating } = useCreateMedia();
const { mutateAsync: updateMedia, isPending: isUpdating } = useUpdateMedia();
const isPending = computed(
  () => isCreating.value || isUpdating.value || !!mediaUploadRef.value?.isUploading,
);

const { handleSubmit } = useForm<UpdateMediaDto>({
  initialValues: {
    title: props.initialMedia?.title ?? '',
  },
  validationSchema: yup.object({
    title: yup.string().required(),
  }),
});

const submit = handleSubmit(async (values) => {
  if (props.initialMedia) {
    const media = await updateMedia({
      id: props.initialMedia.id,
      data: { title: values.title! },
    });
    emit('saved', media);
    return;
  }

  const uploadResult = await mediaUploadRef.value?.upload();
  if (!uploadResult) return;

  const media = await createMedia({
    title: values.title!,
    fileKey: uploadResult.fileKey,
  });
  emit('saved', media);
});
</script>

<i18n lang="json">
{
  "en": {
    "title": "Title",
    "videoFile": "Video file",
    "create": "Create",
    "save": "Save",
    "cancel": "Cancel"
  },
  "de": {
    "title": "Titel",
    "videoFile": "Videodatei",
    "create": "Erstellen",
    "save": "Speichern",
    "cancel": "Abbrechen"
  },
  "es": {
    "title": "Título",
    "videoFile": "Archivo de vídeo",
    "create": "Crear",
    "save": "Guardar",
    "cancel": "Cancelar"
  }
}
</i18n>
